import {
    type Map as MaplibreMap,
    type Popup,
    type PopupOptions,
} from 'maplibre-gl';
import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { ClientDetailModalById } from '@apps/web/components/clients/client_detail_modal_by_id';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';

export type SelectedMapPoint = {
    readonly id: string;
    readonly kind: 'caregiver' | 'client';
    readonly name: string;
    readonly latitude: number;
    readonly longitude: number;
};

export function MapPointPopup({
    map,
    PopupConstructor,
    point,
    organizationId,
    onClose,
}: {
    readonly map: MaplibreMap;
    readonly PopupConstructor: new (options?: PopupOptions) => Popup;
    readonly point: SelectedMapPoint;
    readonly organizationId: string;
    readonly onClose: () => void;
}) {
    const { formatName } = useFormatPersonName();
    const [container] = useState(() => document.createElement('div'));
    const [profileOpen, setProfileOpen] = useState(false);
    useEffect(() => {
        const popup = new PopupConstructor({
            closeButton: true,
            closeOnClick: false,
        })
            .setLngLat([point.longitude, point.latitude])
            .setDOMContent(container)
            .addTo(map);
        popup.on('close', onClose);
        return () => {
            popup.off('close', onClose);
            popup.remove();
        };
    }, [PopupConstructor, map, point, container, onClose]);
    return (
        <Fragment>
            {createPortal(
                <div className="pr-4">
                    <button
                        type="button"
                        className="cursor-pointer text-left font-semibold underline underline-offset-2"
                        onClick={() => setProfileOpen(true)}
                    >
                        {formatName(point.name)}
                    </button>
                    <div className="text-muted-foreground">
                        {point.kind === 'client' ? 'Client' : 'Caregiver'}
                    </div>
                </div>,
                container,
            )}
            {profileOpen && point.kind === 'caregiver' ? (
                <CaregiverDetailModal
                    isOpen={true}
                    caregiverId={point.id}
                    organizationId={organizationId}
                    onClose={() => setProfileOpen(false)}
                />
            ) : null}
            {profileOpen && point.kind === 'client' ? (
                <ClientDetailModalById
                    open={true}
                    clientId={point.id}
                    organizationId={organizationId}
                    onOpenChange={setProfileOpen}
                />
            ) : null}
        </Fragment>
    );
}
